import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { Warning } from '@mui/icons-material'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { ResponsiveTabs, type ResponsiveTabItem } from '@components/common/ResponsiveTabs'
import { SearchField } from '@components/common/SearchField'
import type { AdminUserDto } from '@api/types/admin'
import type { NdaAcceptanceDto, NdaVersionDto } from '@api/types/nda'
import { shortHash } from './ndaText'

interface NdaAcceptanceRecordProps {
  versions: NdaVersionDto[]
  /** The version being examined, or 'all' for every acceptance ever recorded. */
  selectedVersion: number | 'all'
  onSelectedVersionChange: (value: number | 'all') => void
  acceptances: NdaAcceptanceDto[]
  isLoading: boolean
  error: unknown
  currentVersion: number | null
  /** Everybody with an account, for subtracting the signatures. Loaded only when asked for. */
  roster: { users: AdminUserDto[]; truncated: boolean; limit: number } | undefined
  rosterLoading: boolean
  rosterError: unknown
  /** Raised when the outstanding tab is opened, so the roster query can be enabled. */
  onNeedRoster: () => void
}

/**
 * Who has signed what.
 *
 * Deliberately a record rather than a statistic: each row is one person agreeing to one exact
 * text at one time from one address, which is the thing an NDA has to be able to prove after
 * the fact. The count is a by-product.
 *
 * The more operationally useful half is the inverse — who is still stopped at the gate — which
 * is why it sits next to the signatures instead of behind a report.
 */
export function NdaAcceptanceRecord({
  versions,
  selectedVersion,
  onSelectedVersionChange,
  acceptances,
  isLoading,
  error,
  currentVersion,
  roster,
  rosterLoading,
  rosterError,
  onNeedRoster,
}: NdaAcceptanceRecordProps) {
  const [tab, setTab] = useState<'accepted' | 'outstanding'>('accepted')
  // One search across both lists: the question is usually "has this person signed?", and the
  // answer is in whichever tab they turn up in.
  const [search, setSearch] = useState('')

  // Each list keeps its own page, so switching tabs does not land on a page the other list
  // happens not to have.
  const [acceptedPage, setAcceptedPage] = useState(0)
  const [acceptedPageSize, setAcceptedPageSize] = useState(25)
  const [outstandingPage, setOutstandingPage] = useState(0)
  const [outstandingPageSize, setOutstandingPageSize] = useState(25)

  // "Not yet accepted" is only a meaningful question about the version in force: nobody is
  // expected to sign a superseded version, and the API refuses acceptances of one.
  const outstandingAvailable = selectedVersion !== 'all' && selectedVersion === currentVersion
  const activeTab = outstandingAvailable ? tab : 'accepted'

  /** Hash per version, so an acceptance pinned to different text than its version shows up. */
  const hashByVersion = useMemo(() => {
    const map = new Map<number, string>()
    versions.forEach((v) => map.set(v.version, v.contentHash))
    return map
  }, [versions])

  const outstanding = useMemo(() => {
    if (!roster || !outstandingAvailable) return []
    const signed = new Set(acceptances.map((a) => a.userId))
    return roster.users
      .filter((user) => !signed.has(user.id))
      .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''))
  }, [roster, acceptances, outstandingAvailable])

  const needle = search.trim().toLowerCase()
  const filteredAcceptances = useMemo(
    () =>
      needle
        ? acceptances.filter((a) =>
            [a.displayName, a.userId, a.ipAddress].some((v) => v?.toLowerCase().includes(needle))
          )
        : acceptances,
    [acceptances, needle]
  )
  const filteredOutstanding = useMemo(
    () =>
      needle
        ? outstanding.filter((u) =>
            [u.displayName, u.email, u.id].some((v) => v?.toLowerCase().includes(needle))
          )
        : outstanding,
    [outstanding, needle]
  )

  const handleSearch = (value: string) => {
    setSearch(value)
    setAcceptedPage(0)
    setOutstandingPage(0)
  }

  // Stable: ResponsiveTabs calls it from an effect when ?signers= is in the URL on arrival.
  const handleTabChange = useCallback((value: string) => {
    setTab(value === 'outstanding' ? 'outstanding' : 'accepted')
  }, [])

  // The roster is fetched only once the outstanding list is actually on screen. Driven by the
  // effective tab rather than the click so a deep link (?signers=outstanding) also loads it, and
  // a link that lands while a superseded version is selected does not.
  useEffect(() => {
    if (activeTab === 'outstanding') onNeedRoster()
  }, [activeTab, onNeedRoster])

  const tabItems = useMemo<ResponsiveTabItem[]>(
    () => [
      { key: 'accepted', label: 'Accepted', count: acceptances.length },
      {
        key: 'outstanding',
        label: 'Not yet accepted',
        count: outstandingAvailable && roster ? outstanding.length : undefined,
        disabled: !outstandingAvailable,
      },
    ],
    [acceptances.length, outstandingAvailable, roster, outstanding.length]
  )

  const searchBox = (
    <SearchField
      value={search}
      onSearch={handleSearch}
      placeholder={activeTab === 'accepted' ? 'Search name, user id or IP' : 'Search name, email or user id'}
    />
  )

  const acceptedColumns = useMemo<DataTableColumn<NdaAcceptanceDto>[]>(
    () => [
      {
        key: 'person',
        header: 'Person',
        primary: true,
        render: (acceptance) => (
          <>
            <Typography variant="body2">
              {acceptance.displayName || <em>No display name</em>}
            </Typography>
            <Tooltip title={acceptance.userId}>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {acceptance.userId?.slice(0, 8)}…
              </Typography>
            </Tooltip>
          </>
        ),
      },
      {
        key: 'version',
        header: 'Version',
        render: (acceptance) => acceptance.version,
      },
      {
        key: 'acceptedAt',
        header: 'Accepted',
        render: (acceptance) => new Date(acceptance.acceptedAt).toLocaleString(),
      },
      {
        key: 'ip',
        header: 'From',
        hideBelow: 'md',
        render: (acceptance) => (
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {acceptance.ipAddress || '—'}
          </Typography>
        ),
      },
      {
        key: 'client',
        header: 'Client',
        width: 220,
        hideBelow: 'lg',
        render: (acceptance) => (
          <Tooltip title={acceptance.userAgent || 'Not recorded'}>
            <Typography variant="caption" noWrap display="block" sx={{ maxWidth: 220 }}>
              {acceptance.userAgent || '—'}
            </Typography>
          </Tooltip>
        ),
      },
      {
        key: 'hash',
        header: 'Text signed',
        hideBelow: 'md',
        render: (acceptance) => {
          const expected = hashByVersion.get(acceptance.version)
          const mismatched = !!expected && expected !== acceptance.contentHash

          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title={acceptance.contentHash}>
                <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                  {shortHash(acceptance.contentHash)}
                </Typography>
              </Tooltip>
              {mismatched && (
                <Tooltip
                  title={`Signed text does not match version ${acceptance.version} as it stands (${shortHash(expected)}).`}
                >
                  <Warning color="warning" sx={{ fontSize: 16 }} />
                </Tooltip>
              )}
            </Stack>
          )
        },
      },
    ],
    [hashByVersion]
  )

  const outstandingColumns = useMemo<DataTableColumn<AdminUserDto>[]>(
    () => [
      {
        key: 'person',
        header: 'Person',
        primary: true,
        render: (user) => (
          <>
            <Typography variant="body2">{user.displayName || <em>No display name</em>}</Typography>
            <Tooltip title={user.id}>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                {user.id?.slice(0, 8)}…
              </Typography>
            </Tooltip>
          </>
        ),
      },
      {
        key: 'email',
        header: 'Email',
        render: (user) => user.email,
      },
      {
        key: 'roles',
        header: 'Roles',
        hideBelow: 'md',
        render: (user) => (
          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            {(user.roles || []).map((role) => (
              <Chip key={role} size="small" variant="outlined" label={role} />
            ))}
          </Stack>
        ),
      },
      {
        key: 'lastLogin',
        header: 'Last signed in',
        hideBelow: 'md',
        render: (user) => (user.lastLoginAt ? new Date(user.lastLoginAt).toLocaleString() : 'Never'),
      },
      {
        key: 'account',
        header: 'Account',
        render: (user) =>
          user.isActive ? (
            <Chip size="small" label="Active" color="success" variant="outlined" />
          ) : (
            <Tooltip title="Disabled accounts cannot sign in, so they will never accept.">
              <Chip size="small" label="Disabled" variant="outlined" />
            </Tooltip>
          ),
      },
    ],
    []
  )

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6">Acceptance record</Typography>
          <Typography variant="body2" color="text.secondary">
            Append-only. Each row is one person, one version, one moment — names and text hashes
            are snapshots taken at the time of signing, not lookups of today&apos;s profile.
          </Typography>
        </Box>

        {/* Hidden until the versions are known: a value with no matching option makes the
            Select complain and renders blank. */}
        {versions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: { sm: 180 }, flexShrink: 0 }}>
            <InputLabel id="nda-acceptance-version">Version</InputLabel>
            <Select
              labelId="nda-acceptance-version"
              label="Version"
              value={selectedVersion}
              onChange={(e) => {
                onSelectedVersionChange(e.target.value === 'all' ? 'all' : Number(e.target.value))
                setAcceptedPage(0)
                setOutstandingPage(0)
              }}
            >
              {versions.map((v) => (
                <MenuItem key={v.id} value={v.version}>
                  Version {v.version}
                  {v.isCurrent ? ' (in force)' : ''}
                </MenuItem>
              ))}
              <MenuItem value="all">All versions</MenuItem>
            </Select>
          </FormControl>
        )}
      </Stack>

      {/* Own param name: the page may grow other tabs, and ?tab= belongs to the page. */}
      <ResponsiveTabs
        tabs={tabItems}
        value={activeTab}
        onChange={handleTabChange}
        paramName="signers"
        sx={{ mb: 2 }}
      />

      {!outstandingAvailable && currentVersion !== null && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          Who has not signed is only asked about version {currentVersion}, the one in force —
          an older version can no longer be accepted.
        </Typography>
      )}

      {/* On the accepted tab DataTable draws its own skeletons and error; the outstanding tab is
          waiting on the same query, so it needs them here. */}
      {activeTab === 'outstanding' && isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {activeTab === 'outstanding' && !!error && !isLoading && (
        <Alert severity="error">
          Failed to load the acceptance record. {(error as Error)?.message}
        </Alert>
      )}

      {!isLoading && !error && activeTab === 'accepted' && acceptances.length === 0 ? (
        <Alert severity="info">Nobody has accepted this version yet.</Alert>
      ) : activeTab === 'accepted' ? (
        <DataTable
          rows={filteredAcceptances}
          columns={acceptedColumns}
          toolbar={searchBox}
          emptyMessage={`Nobody matches "${search}".`}
          getRowKey={(acceptance) => String(acceptance.id)}
          loading={isLoading}
          error={error ? `Failed to load the acceptance record. ${(error as Error)?.message ?? ''}` : null}
          dense
          // No `total` => DataTable pages the rows it was given.
          page={acceptedPage}
          pageSize={acceptedPageSize}
          onPageChange={setAcceptedPage}
          onPageSizeChange={setAcceptedPageSize}
          pageSizeOptions={[25, 50, 100]}
        />
      ) : null}

      {/* Held until the acceptances are in: "outstanding" is the roster minus the signatures, so
          rendering it against a half-loaded acceptance list would name people who have signed. */}
      {!isLoading && !error && activeTab === 'outstanding' && (
        <>
          {rosterLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress size={28} />
            </Box>
          )}

          {!!rosterError && !rosterLoading && (
            <Alert severity="error">
              Failed to load the user list, so who is outstanding cannot be worked out.{' '}
              {(rosterError as Error)?.message}
            </Alert>
          )}

          {!rosterLoading && !rosterError && roster && (
            <>
              {roster.truncated && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Only the first {roster.limit} accounts were read, so this list may be incomplete.
                </Alert>
              )}

              {outstanding.length === 0 ? (
                <Alert severity="success">
                  Everybody with an account has accepted version {currentVersion}.
                </Alert>
              ) : (
                <DataTable
                  rows={filteredOutstanding}
                  columns={outstandingColumns}
                  toolbar={searchBox}
                  emptyMessage={`Nobody outstanding matches "${search}".`}
                  getRowKey={(user) => user.id}
                  dense
                  page={outstandingPage}
                  pageSize={outstandingPageSize}
                  onPageChange={setOutstandingPage}
                  onPageSizeChange={setOutstandingPageSize}
                  pageSizeOptions={[25, 50, 100]}
                />
              )}
            </>
          )}
        </>
      )}
    </Paper>
  )
}
